import type { ThreadMessageLike } from "@assistant-ui/react";
import { getEnv } from "../env";
import { quitarContextoEnlace } from "../context";
import { createModelosApi } from "./modelosApi";
import {
  fundirMetadata,
  metadataModelo,
  metadataSugeridas,
  modeloDeMensaje,
  preguntasSugeridasDe,
} from "../preguntas";

const API_URL = getEnv("VITE_API_URL") || "http://localhost:8000";

export class ApiError extends Error {
  status: number;
  /**
   * `detail` de FastAPI, cuando el cuerpo lo trae como texto.
   *
   * Existe porque hay endpoints cuyo mensaje ES la informacion accionable: los
   * tres 400 del PUT de asignaciones (no servible / deshabilitado / sin
   * `tools`) dicen cosas distintas a proposito, porque cada una se arregla de
   * una forma, y el 409 del borrado dice si el modelo esta asignado o fijado en
   * una conversacion. Sin esto solo llegaria el status y la UI tendria que
   * inventarse un mensaje generico.
   */
  detail: string | null;
  constructor(message: string, status: number, detail: string | null = null) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

type GetToken = () => Promise<string>;

export interface ContextField {
  key: string;
  label: string;
  type: "string" | "text";
  required: boolean;
}

export interface Widget {
  id: number;
  widget_uid: string;
  name: string;
  agent_id: string;
  context_schema: ContextField[];
  context_prompt_template: string | null;
  thread_ttl_hours: number;
  allowed_origins: string[];
  active: boolean;
  /** El enlace directo arranca la conversacion solo con sus datos. */
  auto_start: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface WidgetCreated extends Widget {
  snippet: string;
  link_url: string;
}

export type BulkAssignBody = {
  emails: string[];
  agents: string[];
  op: "add" | "remove";
  validate: boolean;
};

export type BulkAssignResult = {
  updated: { email: string; allowed_agents: string[] }[];
  invalid: string[];
};

export interface ModeloCatalogo {
  model_id: string;
  label?: string | null;
}

export interface Assistant {
  graph_id: string;
  label: string;
  description: string;
  ui_enabled: boolean;
  /** Modelo con el que el agente corre hoy (asignacion del panel o default). */
  model_default: string;
  /**
   * Catalogo habilitado para el selector por conversacion. **Solo para
   * admins**: el backend lo omite para el resto porque revela que modelos se
   * pagan. Por eso es opcional aqui, y no un array vacio.
   */
  models_disponibles?: ModeloCatalogo[];
}

/**
 * `config` del run con el modelo elegido, o `null` si no hay nada que mandar.
 *
 * La RUTA de claves es el contrato: el backend lee
 * `config.configurable.model_override` y, si no la encuentra, **descarta en
 * silencio por diseño** (`_modelo_pedido` devuelve `None` sin error, para que un
 * cliente desactualizado no rompa la conversacion). Consecuencia: una sola letra
 * mal aqui apaga la funcionalidad entera SIN NINGUNA señal — el admin elige un
 * modelo, ve el selector cambiado, y la conversacion corre con el default para
 * siempre.
 *
 * Es una funcion propia, y no un literal en medio de `ChatRuntime`, porque asi
 * la ruta se puede fijar con un test sin montar el thread.
 */
export function configDeModelo(
  pendiente: string | null,
): { configurable: { model_override: string } } | null {
  if (pendiente === null) return null;
  return { configurable: { model_override: pendiente } };
}

export interface WidgetConfig {
  widget_uid: string;
  agent_id: string;
  agent_description?: string;
  context_schema: ContextField[];
  context_prompt_template: string | null;
  thread_ttl_hours: number;
  /** El enlace directo arranca la conversacion solo. Ausente en un backend anterior. */
  auto_start?: boolean;
}

function createApiClient(getToken: GetToken, onAuthFailure?: () => void) {
  async function headers(contentType = "application/json") {
    const token = await getToken();
    const h: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    if (contentType) h["Content-Type"] = contentType;
    return h;
  }

  // Retry once on 401 (token may have expired between check and request)
  async function fetchWithAuth(
    url: string,
    init?: RequestInit,
    contentType = "application/json",
  ): Promise<Response> {
    const res = await fetch(url, { ...init, headers: await headers(contentType) });
    if (res.status === 401) {
      // Token was rejected — retry with fresh headers (getToken checks expiry)
      const retryRes = await fetch(url, { ...init, headers: await headers(contentType) });
      if (retryRes.status === 401) {
        onAuthFailure?.();
        throw new Error("Authentication failed");
      }
      return retryRes;
    }
    return res;
  }

  async function listAssistants(uiOnly = true): Promise<Assistant[]> {
    const res = await fetchWithAuth(`${API_URL}/assistants?ui_only=${uiOnly}`);
    if (!res.ok) throw new Error(`Failed to list assistants: ${res.status}`);
    return res.json();
  }

  /**
   * Metadata del thread, con el `model_override` que un admin le haya fijado.
   *
   * Sin leerlo, al abrir una conversacion existente el selector mostraria "por
   * defecto" mientras la conversacion corre con otro modelo — el backend
   * RELEE el override guardado aunque el front no lo mande.
   */
  async function getThread(
    threadId: string,
  ): Promise<{ thread_id: string; model_override: string | null }> {
    const res = await fetchWithAuth(`${API_URL}/threads/${threadId}`);
    if (!res.ok) throw new Error(`Failed to get thread: ${res.status}`);
    return res.json();
  }

  async function createThread(): Promise<{ thread_id: string }> {
    const res = await fetchWithAuth(`${API_URL}/threads`, { method: "POST" });
    if (!res.ok) throw new Error(`Failed to create thread: ${res.status}`);
    return res.json();
  }

  async function getThreadState(
    threadId: string,
    graphId: string
  ): Promise<{ thread_id: string; state: Record<string, unknown> }> {
    const url = new URL(`${API_URL}/threads/${threadId}/state`);
    url.searchParams.set("graph_id", graphId);
    const res = await fetchWithAuth(url.toString());
    if (!res.ok) throw new Error(`Failed to get thread state: ${res.status}`);
    return res.json();
  }

  async function listThreads(
    limit = 20,
    offset = 0,
  ): Promise<{ thread_id: string; last_checkpoint: string; assistant_id?: string; created_at?: string; title?: string }[]> {
    const res = await fetchWithAuth(`${API_URL}/threads?limit=${limit}&offset=${offset}`);
    if (!res.ok) throw new Error(`Failed to list threads: ${res.status}`);
    return res.json();
  }

  async function sendMessage(params: {
    assistantId: string;
    threadId?: string;
    input: Record<string, unknown>;
    streamMode?: string[];
    config?: Record<string, unknown>;
  }): Promise<Response> {
    const body: Record<string, unknown> = {
      assistant_id: params.assistantId,
      input: params.input,
      stream_mode: params.streamMode ?? ["messages", "updates"],
    };
    // Omit thread_id in auto mode — backend generates it and returns it in metadata/end
    if (params.threadId) body.thread_id = params.threadId;
    if (params.config) body.config = params.config;

    const res = await fetchWithAuth(`${API_URL}/runs/stream`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new ApiError(`Failed to send message: ${res.status}`, res.status);
    return res;
  }

  async function uploadFiles(params: {
    files: File[];
    assistantId?: string;
    message?: string;
    threadId?: string;
    /**
     * Uid del widget embebido, cuando el run lo lanza un widget. Aqui el body es
     * un multipart, no JSON, asi que no hay `config.metadata.widget_uid` donde
     * meterlo como en `sendMessage`: viaja como campo del formulario y
     * `upload_run` lo reconstruye ahi (`back/src/api/runs.py`). Sin el, una
     * subida del widget se trazaba como `source:frontend` y pagaba el extractor
     * del formulario sugerido, que el widget no pinta.
     */
    widgetUid?: string;
  }): Promise<Response> {
    const form = new FormData();
    for (const file of params.files) {
      form.append("files", file);
    }
    if (params.assistantId) form.append("assistant_id", params.assistantId);
    if (params.message) form.append("message", params.message);
    if (params.threadId) form.append("thread_id", params.threadId);
    // Solo cuando hay valor: el chat principal no tiene widget y el campo debe
    // quedar AUSENTE para que el `Form(default=None)` del backend lo vea como
    // "no viene de un widget".
    if (params.widgetUid) form.append("widget_uid", params.widgetUid);

    const res = await fetchWithAuth(`${API_URL}/runs/upload`, {
      method: "POST",
      body: form,
    }, "");
    if (!res.ok) throw new ApiError(`Failed to upload files: ${res.status}`, res.status);
    return res;
  }

  async function getThreadMessages(
    threadId: string,
    graphId: string,
  ): Promise<ThreadMessageLike[]> {
    const res = await fetchWithAuth(
      `${API_URL}/threads/${threadId}/state?graph_id=${graphId}`,
    );
    if (!res.ok) throw new Error(`Failed to get thread state: ${res.status}`);
    const data = await res.json();
    const rawMessages: Record<string, unknown>[] = data.state?.messages ?? [];
    // Formulario SUGERIDO persistido: `suggested_questions` = `{message_id,
    // preguntas}` o `null`. Viene FUERA de `state` porque no es state del grafo:
    // es la ayuda de interfaz derivada de la prosa. El backend solo lo devuelve
    // cuando `message_id` es el del ULTIMO mensaje del thread, asi que un valor
    // no nulo pertenece al ultimo mensaje del asistente; abajo se cuelga de el.
    const sugeridas = preguntasSugeridasDe(data.suggested_questions);

    const ATTACHMENT_RE = /^\[attachment:(.+)\]$/;
    // Text-file parts are stored as "[file:name]\n<inline content>" (see
    // _build_multimodal_message in back/src/api/runs.py). The content is for
    // the LLM only — render just an attachment chip, never the raw body.
    const FILE_RE = /^\[file:(.+?)\]\n/;

    // Build a map of tool_call_id → result from ToolMessages
    const toolResults = new Map<string, unknown>();
    for (const m of rawMessages) {
      if ((m.type as string) === "tool" && m.tool_call_id) {
        toolResults.set(m.tool_call_id as string, m.content ?? "");
      }
    }

    const result: ThreadMessageLike[] = [];

    for (const m of rawMessages) {
      const type = (m.type as string) || "";

      // Skip system and tool messages (tool results are merged into ai messages)
      if (type !== "human" && type !== "ai") continue;

      const content = m.content;

      // --- Human messages ---
      if (type === "human") {
        let text = "";
        // assistant-ui's attachment shape (CompleteAttachment) is built ad-hoc
        // here from raw thread state; typing it fully would mean reconstructing
        // a deep library union. Kept loose at the construction site.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const attachments: any[] = [];

        if (typeof content === "string") {
          text = content;
        } else if (Array.isArray(content)) {
          const textParts: string[] = [];
          const seenAttachments = new Set<string>();
          let standaloneImageUrl: string | null = null;
          for (const p of content as Record<string, unknown>[]) {
            if (p.type === "text" && typeof p.text === "string") {
              const match =
                (p.text as string).match(ATTACHMENT_RE) ??
                (p.text as string).match(FILE_RE);
              if (match) {
                // The [attachment:] label may carry a " url:..." suffix — the
                // URL is for the LLM, only the filename goes in the chip
                const name = match[1].split(" url:")[0];
                // Deduplicate: one chip per unique filename (multi-page files
                // produce one [attachment:X] label per page — show only once)
                if (!seenAttachments.has(name)) {
                  seenAttachments.add(name);
                  const isImage = /\.(png|jpe?g|gif|webp)$/i.test(name);
                  const contentType = isImage
                    ? "image/png"
                    : /\.docx$/i.test(name)
                      ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      : /\.xlsx$/i.test(name)
                        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        : /\.pdf$/i.test(name)
                          ? "application/pdf"
                          : "text/plain";
                  attachments.push({
                    id: crypto.randomUUID(),
                    type: isImage ? "image" : "document",
                    name,
                    contentType,
                    status: { type: "complete" },
                    content: [{ type: "text", text: name }],
                  });
                }
              } else {
                textParts.push(p.text as string);
              }
            }
            if (p.type === "image_url") {
              const imageUrl = p.image_url as { url?: string } | undefined;
              if (imageUrl?.url) standaloneImageUrl = imageUrl.url;
            }
          }
          // Standalone image (user uploaded a raw image, no [attachment:] label)
          if (standaloneImageUrl && attachments.length === 0) {
            attachments.push({
              id: crypto.randomUUID(),
              type: "image",
              name: "Archivo adjunto",
              contentType: "image/png",
              status: { type: "complete" },
              content: [{ type: "image", image: standaloneImageUrl }],
            });
          }
          // For image attachments only, add the thumbnail preview
          for (const att of attachments) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (att.type === "image" && standaloneImageUrl && !att.content.some((c: any) => c.type === "image")) {
              att.content.push({ type: "image", image: standaloneImageUrl });
            }
          }
          text = textParts.join("");
        }
        // El contexto de un enlace directo viaja delante del primer mensaje,
        // pero no lo escribio el usuario: no se enseña al recargar.
        text = quitarContextoEnlace(text);

        if (!text && attachments.length === 0) continue;

        result.push({
          role: "user" as const,
          content: [{ type: "text" as const, text }],
          ...(attachments.length > 0 ? { attachments } : {}),
        });
        continue;
      }

      // --- AI messages ---
      // Mix of text and tool-call parts assigned to ThreadMessageLike.content
      // (a deep assistant-ui union); built loose, validated by the consumer.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const parts: any[] = [];

      // Extract text content
      let aiText = "";
      if (typeof content === "string") {
        aiText = content;
      } else if (Array.isArray(content)) {
        aiText = (content as Record<string, unknown>[])
          .filter((p) => p.type === "text")
          .map((p) => p.text as string)
          .join("");
      }
      if (aiText) {
        parts.push({ type: "text" as const, text: aiText });
      }

      // Extract tool calls with their results
      const toolCalls = m.tool_calls as { id?: string; name?: string; args?: Record<string, unknown> }[] | undefined;
      if (Array.isArray(toolCalls)) {
        for (const tc of toolCalls) {
          const tcId = tc.id ?? `tc_${tc.name}_${Date.now()}`;
          parts.push({
            type: "tool-call" as const,
            toolCallId: tcId,
            toolName: tc.name ?? "unknown",
            args: tc.args ?? {},
            argsText: JSON.stringify(tc.args ?? {}),
            result: toolResults.get(tcId),
          });
        }
      }

      if (parts.length === 0) continue;

      // Modelo con el que corrio ESTE mensaje, para el badge. Viaja en
      // `response_metadata.model_name` (lo escribe `langchain_openai` y
      // `GET /threads/{id}/state` lo sirve via `model_dump`), asi que el badge
      // sobrevive a recargar la pagina. `modeloDeMensaje` devuelve `null` si no
      // consta: los mensajes anteriores a esto no lo traen y no se inventa.
      const modelo = modeloDeMensaje(m);

      result.push({
        role: "assistant" as const,
        content: parts,
        ...(modelo ? { metadata: metadataModelo(modelo) } : {}),
      });
    }

    // El formulario sugerido cuelga del ULTIMO mensaje pintado, y solo si (a) es
    // del asistente y (b) su `id` es el `message_id` del formulario. El backend
    // ya garantiza (b), pero el id esta aqui mismo y comprobarlo hace al front
    // autosuficiente: si el ultimo es humano o el formulario es de otro turno,
    // el usuario ya contesto (o la conversacion siguio) y no se pinta nada.
    // Viaja en `metadata.custom`, la MISMA forma que produce el stream en vivo
    // (`components/chat/sse.ts`); `fromThreadMessageLike` de assistant-ui la
    // conserva en `initialMessages`.
    const ultimo = result[result.length - 1];
    const ultimoRawId = rawMessages[rawMessages.length - 1]?.id;
    if (sugeridas && ultimo?.role === "assistant" && ultimoRawId === sugeridas.message_id) {
      // Se FUNDE con la metadata que ya tuviera (el modelo del mensaje, para el
      // badge): las dos viven en el mismo `custom` y un reemplazo a secas
      // borraria el badge justo en el ultimo mensaje, que es donde mas se mira.
      result[result.length - 1] = {
        ...ultimo,
        metadata: fundirMetadata(ultimo.metadata, metadataSugeridas(sugeridas)),
      };
    }

    return result;
  }

  async function scoreThread(
    threadId: string,
    rating: "ok" | "ko",
    comment?: string,
    traceId?: string,
  ): Promise<void> {
    const res = await fetchWithAuth(`${API_URL}/threads/${threadId}/score`, {
      method: "POST",
      body: JSON.stringify({ rating, comment, trace_id: traceId }),
    });
    if (!res.ok) throw new Error(`Failed to score thread: ${res.status}`);
  }

  // --- Admin API ---

  async function listUsers(): Promise<
    { email: string; allowed_agents: string[]; created_at: string | null; updated_at: string | null }[]
  > {
    const res = await fetchWithAuth(`${API_URL}/admin/users`);
    if (!res.ok) throw new Error(`Failed to list users: ${res.status}`);
    return res.json();
  }

  async function upsertUser(
    email: string,
    allowedAgents: string[],
  ): Promise<{ email: string; allowed_agents: string[] }> {
    const res = await fetchWithAuth(`${API_URL}/admin/users/${encodeURIComponent(email)}`, {
      method: "PUT",
      body: JSON.stringify({ allowed_agents: allowedAgents }),
    });
    if (!res.ok) throw new Error(`Failed to update user: ${res.status}`);
    return res.json();
  }

  async function deleteUser(email: string): Promise<void> {
    const res = await fetchWithAuth(`${API_URL}/admin/users/${encodeURIComponent(email)}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error(`Failed to delete user: ${res.status}`);
  }

  async function bulkAssignUsers(body: BulkAssignBody): Promise<BulkAssignResult> {
    const res = await fetchWithAuth(`${API_URL}/admin/users/bulk`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Failed bulk assign: ${res.status}`);
    return res.json();
  }

  // --- Admin API Keys ---

  async function listApiKeys(): Promise<
    { id: number; client_name: string; key_id: string; allowed_origins: string[]; guardrails: boolean; active: boolean; created_at: string | null; last_used_at: string | null }[]
  > {
    const res = await fetchWithAuth(`${API_URL}/admin/api-keys`);
    if (!res.ok) throw new Error(`Failed to list API keys: ${res.status}`);
    return res.json();
  }

  async function createApiKey(
    clientName: string,
    allowedOrigins: string[],
    guardrails: boolean,
  ): Promise<{ id: number; client_name: string; key_id: string; secret: string; allowed_origins: string[]; guardrails: boolean; active: boolean; created_at: string | null; warning: string }> {
    const res = await fetchWithAuth(`${API_URL}/admin/api-keys`, {
      method: "POST",
      body: JSON.stringify({ client_name: clientName, allowed_origins: allowedOrigins, guardrails }),
    });
    if (!res.ok) throw new Error(`Failed to create API key: ${res.status}`);
    return res.json();
  }

  async function updateApiKey(
    id: number,
    data: { client_name?: string; allowed_origins?: string[]; guardrails?: boolean; active?: boolean },
  ): Promise<void> {
    const res = await fetchWithAuth(`${API_URL}/admin/api-keys/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Failed to update API key: ${res.status}`);
  }

  async function deleteApiKey(id: number): Promise<void> {
    const res = await fetchWithAuth(`${API_URL}/admin/api-keys/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error(`Failed to delete API key: ${res.status}`);
  }

  async function regenerateApiKey(id: number): Promise<{ id: number; secret: string; warning: string }> {
    const res = await fetchWithAuth(`${API_URL}/admin/api-keys/${id}/regenerate`, {
      method: "POST",
    });
    if (!res.ok) throw new Error(`Failed to regenerate API key: ${res.status}`);
    return res.json();
  }

  // --- Admin Widgets ---

  async function listWidgets(): Promise<Widget[]> {
    const res = await fetchWithAuth(`${API_URL}/admin/widgets`);
    if (!res.ok) throw new Error(`Failed to list widgets: ${res.status}`);
    return res.json();
  }

  async function createWidget(data: {
    name: string;
    agent_id: string;
    context_schema?: ContextField[];
    context_prompt_template?: string;
    thread_ttl_hours?: number;
    allowed_origins?: string[];
    auto_start?: boolean;
  }): Promise<WidgetCreated> {
    const res = await fetchWithAuth(`${API_URL}/admin/widgets`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Failed to create widget: ${res.status}`);
    return res.json();
  }

  async function updateWidget(
    id: number,
    data: {
      name?: string;
      context_schema?: ContextField[];
      context_prompt_template?: string;
      thread_ttl_hours?: number;
      allowed_origins?: string[];
      active?: boolean;
      auto_start?: boolean;
    },
  ): Promise<{ id: number; updated: boolean }> {
    const res = await fetchWithAuth(`${API_URL}/admin/widgets/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Failed to update widget: ${res.status}`);
    return res.json();
  }

  async function deleteWidget(id: number): Promise<{ deleted: number }> {
    const res = await fetchWithAuth(`${API_URL}/admin/widgets/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error(`Failed to delete widget: ${res.status}`);
    return res.json();
  }

  async function getWidgetSnippet(id: number): Promise<{ html: string; link_url: string }> {
    const res = await fetchWithAuth(`${API_URL}/admin/widgets/${id}/snippet`);
    if (!res.ok) throw new Error(`Failed to get snippet: ${res.status}`);
    return res.json();
  }

  async function getWidgetConfig(widgetUid: string): Promise<WidgetConfig> {
    const res = await fetch(`${API_URL}/widgets/${widgetUid}`);
    if (!res.ok) throw new Error(`Widget not found: ${res.status}`);
    return res.json();
  }

  async function getInfo(): Promise<{ version: string; graphs: string[] }> {
    const res = await fetchWithAuth(`${API_URL}/info`);
    if (!res.ok) throw new Error(`Failed to get info: ${res.status}`);
    return res.json();
  }

  return {
    /**
     * Endpoints de modelos LLM (panel de admin), en su propio modulo pero
     * compartiendo ESTE `fetchWithAuth`: el retry del 401 y el `onAuthFailure`
     * son uno solo para todo el cliente.
     */
    modelos: createModelosApi(fetchWithAuth, API_URL),
    listAssistants,
    createThread,
    getThread,
    getThreadState,
    getThreadMessages,
    listThreads,
    sendMessage,
    uploadFiles,
    scoreThread,
    listUsers,
    upsertUser,
    deleteUser,
    bulkAssignUsers,
    listApiKeys,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    regenerateApiKey,
    listWidgets,
    createWidget,
    updateWidget,
    deleteWidget,
    getWidgetSnippet,
    getWidgetConfig,
    getInfo,
  };
}

export { createApiClient };
export type ApiClient = ReturnType<typeof createApiClient>;
